import React, { FC, Suspense, useRef, useState, useTransition } from 'react';
import { Icon } from 'src/components/widgets/icon';
import PlusIcon from 'src/shared/material_icons/content/add.svg';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { Drawer } from 'src/components/widgets/drawer';
import { ZTooltip } from 'src/components/widgets/ztooltip';
import { useRelayStore } from 'src/relay/environment';
import { T, backofficeUrl, toLocalID } from 'src/shared/helpers';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import { CONDITION_OPERATORS, buildFormula, parseFormula } from '../common/constants';
import {
    AddQuantityButton,
    AddRuleButton,
    AddTextButton,
    ConditionFieldTitle,
    ConditionFormRow,
    ConditionLabel,
    DeleteRuleButton,
    EmptyConditions,
    EmptyRulesCenter,
    EmptyRulesSmall,
    ExpandArrow,
    FooterButton,
    FooterButtonTooltip,
    FormGridRow,
    FormLabel,
    FormLabelCompact,
    FormSelect,
    FormSelectCompact,
    FullModeHint,
    FullModeIframeContainer,
    FullModeLink,
    FullModeSkeleton,
    IframeEmbed,
    PriceInput,
    PricingRuleCard,
    PricingRuleCardBody,
    PricingRuleCardHeader,
    QuantityTierContainer,
    QuantityTierRow,
    RuleElementTitle,
    RuleHeaderActions,
    RuleMetaLine,
    RulePropertyName,
    SectionCard,
    SectionCardTitle,
    SettingHint,
    SharedWarningCallout,
    StackedBody,
    StackedBreadcrumb,
    StackedFooterButtons,
    TargetsInfo,
    TargetsSection
} from '../common/styled';
import type { PricingConditionData, PricingGroupData, PricingRuleData } from '../common/types';
import type { pricingRuleEditor_PricingDetailQuery } from './__generated__/pricingRuleEditor_PricingDetailQuery.graphql';
import type { pricingRuleEditor_UpdatePricingMutation } from './__generated__/pricingRuleEditor_UpdatePricingMutation.graphql';
import { FullModeEditorSkeleton, PanelFormSkeleton } from '../common/skeletons';
import { PlanUpgradeBanner } from '../common/planUpgradeBanner';

export interface PricingRuleEditorProps {
    pricingId: string;
    productId: string;
    onBack: () => void;
    onGoToFullTool: () => void;
}

const pricingDetailQuery = graphql`
    query pricingRuleEditor_PricingDetailQuery($id: ID!) {
        pricing(id: $id) {
            id
            pricingID
            pricingName
            infoPricingText
            priceDescriptionEnabled
            quantityAsUnit
            priceStructure
            products {
                productID
            }
            rules {
                ruleID
                propertyID
                elementID
                elementTitle
                isSetupPrice
                allMatchType
                priceType
                simplified
                conditions {
                    conditionID
                    formula
                    position
                    priceToAdd
                    quantity
                }
                targets {
                    targetID
                    area
                    color
                    side
                }
            }
        }
        pricingProperties {
            propertyID
            propertyName
            functionName
            elements {
                elementID
                elementName
            }
        }
    }
`;

const productNameQuery = graphql`
    query pricingRuleEditor_ProductNameQuery($id: ID!) {
        node(id: $id) {
            ... on Model {
                name
            }
        }
    }
`;

const PricingRuleEditorContent: FC<PricingRuleEditorProps> = ({ pricingId, productId, onBack, onGoToFullTool }) => {
    const invalidate = useRelayStore((s) => s.invalidateRelayStore);
    const { subscription } = useSubscriptionData();
    const isPlanSupported = subscription?.isAdvancedPricingEnabled ?? false;
    const [saving, setSaving] = useState(false);
    const [fullMode, setFullMode] = useState(false);
    const [iframeLoading, setIframeLoading] = useState(true);
    const [expandedRules, setExpandedRules] = useState<Set<string>>(new Set());
    const [dirty, setDirty] = useState(false);
    const [, startSaveTransition] = useTransition();

    const json = useLazyLoadQuery<pricingRuleEditor_PricingDetailQuery>(pricingDetailQuery, { id: pricingId });
    const productData = useLazyLoadQuery<any>(productNameQuery, { id: productId });
    const productName = (productData?.node as any)?.name || '';
    const assignedProductCount = json?.pricing?.products?.length;

    const [properties] = useState<
        ReadonlyArray<{ propertyID: number; propertyName: string; functionName: string; elements: ReadonlyArray<{ elementID: number; elementName: string }> }>
    >(() => json?.pricingProperties || []);
    const [pricingGroup, setPricingGroup] = useState<PricingGroupData | null>(() => {
        const pg = json?.pricing;
        const props = json?.pricingProperties || [];
        if (!pg) return null;
        return {
            id: pg.id,
            pricingName: pg.pricingName,
            infoPricingText: pg.infoPricingText || '',
            priceDescriptionEnabled: pg.priceDescriptionEnabled ?? false,
            quantityAsUnit: pg.quantityAsUnit ?? false,
            priceStructure: pg.priceStructure || 'FINAL_PRICE',
            rules: (pg.rules || []).map((rule: any) => ({
                ...rule,
                tempID: crypto.randomUUID(),
                simplified: rule.simplified ?? false,
                propertyName: props.find((pricing: any) => pricing.propertyID === rule.propertyID)?.propertyName || `Property #${rule.propertyID}`,
                conditions: (rule.conditions || []).map((condition: any) => ({
                    ...condition,
                    tempID: crypto.randomUUID(),
                    priceToAdd: String(condition.priceToAdd ?? '0'),
                    quantity: String(condition.quantity ?? '0')
                }))
            }))
        };
    });

    const pricingGroupRef = useRef(pricingGroup);
    pricingGroupRef.current = pricingGroup;

    const pricingName = pricingGroup?.pricingName || '';

    const [commitUpdatePricing] = useMutation<pricingRuleEditor_UpdatePricingMutation>(graphql`
        mutation pricingRuleEditor_UpdatePricingMutation($input: PricingGroupInput!) {
            updatePricing(input: $input) {
                pricingGroup {
                    id
                }
            }
        }
    `);

    const toggleRule = (tempID: string) => {
        setExpandedRules((prev) => {
            const next = new Set(prev);
            next.has(tempID) ? next.delete(tempID) : next.add(tempID);
            return next;
        });
    };

    const updatePricingField = <K extends keyof PricingGroupData>(field: K, value: PricingGroupData[K]) => {
        setPricingGroup((prev) => {
            if (!prev) return prev;
            return { ...prev, [field]: value };
        });
        setDirty(true);
    };

    const updateRuleField = <K extends keyof PricingRuleData>(tempID: string, field: K, value: PricingRuleData[K]) => {
        setPricingGroup((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                rules: prev.rules.map((rule) => (rule.tempID === tempID ? { ...rule, [field]: value } : rule))
            };
        });
        setDirty(true);
    };

    const updateConditionField = (ruleTempID: string, condTempID: string, field: keyof PricingConditionData, value: string) => {
        setPricingGroup((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                rules: prev.rules.map((rule) =>
                    rule.tempID === ruleTempID
                        ? {
                              ...rule,
                              conditions: rule.conditions.map((condition) => (condition.tempID === condTempID ? { ...condition, [field]: value } : condition))
                          }
                        : rule
                )
            };
        });
        setDirty(true);
    };

    const getPropertyKeyword = (propertyID: number): string => {
        const prop = properties.find((p) => p.propertyID === propertyID);
        if (!prop) return '';
        switch (prop.functionName) {
            case 'ElementSize':
            case 'DesignSize':
            case 'CanvasSize':
                return T._('size', 'Admin');
            case 'TextLength':
            case 'DesignTextLength':
                return T._('texts', 'Admin');
            case 'LinesLength':
                return T._('lines', 'Admin');
            case 'AmountOfElements':
                return T._('elements', 'Admin');
            case 'AmountOfUsedColors':
                return T._('colors', 'Admin');
            case 'CustomizedSides':
                return T._('sides', 'Admin');
            default:
                return prop.propertyName;
        }
    };

    const deleteRule = (tempID: string) => {
        setPricingGroup((prev) => {
            if (!prev) return prev;
            return { ...prev, rules: prev.rules.filter((rule) => rule.tempID !== tempID) };
        });
        setDirty(true);
    };

    const handleSave = () => {
        const pg = pricingGroupRef.current;
        if (!pg) return;
        setSaving(true);
        const input = {
            id: pg.id,
            pricingName: { value: pg.pricingName },
            infoPricingText: { value: pg.infoPricingText },
            priceDescriptionEnabled: { value: pg.priceDescriptionEnabled },
            quantityAsUnit: { value: pg.quantityAsUnit },
            priceStructure: { value: pg.priceStructure },
            rules: {
                value: pg.rules.map((rule) => ({
                    ruleID: rule.ruleID,
                    propertyID: { value: rule.propertyID },
                    elementID: { value: rule.elementID },
                    elementTitle: { value: rule.elementTitle },
                    isSetupPrice: { value: rule.isSetupPrice },
                    allMatchType: { value: rule.allMatchType },
                    priceType: { value: rule.priceType },
                    conditions: {
                        value: rule.conditions.map((condition) => ({
                            conditionID: condition.conditionID,
                            priceToAdd: { value: parseFloat(condition.priceToAdd) || 0 },
                            position: { value: condition.position },
                            formula: { value: condition.formula },
                            quantity: { value: parseInt(condition.quantity) || 0 }
                        }))
                    }
                }))
            }
        };

        commitUpdatePricing({
            variables: { input: input as any },
            updater: (store) => store.invalidateStore(),
            onCompleted: () => {
                invalidate();
                startSaveTransition(() => {
                    onBack();
                });
            },
            onError: (error) => {
                console.error('Pricing save error:', error);
                setSaving(false);
            }
        });
    };

    return (
        <Drawer
            isOpen={true}
            isStacked
            onClose={onBack}
            header={{ title: pricingName, subtitle: fullMode ? T._('Pricing rules — Full mode', 'Admin') : T._('Pricing rules', 'Admin') }}
            width={fullMode ? window.innerWidth - 10 : 620}
            footer={
                <>
                    {!fullMode && <FullModeHint>{T._('+ more settings available in full mode', 'Admin')}</FullModeHint>}
                    <FullModeLink
                        onClick={() => {
                            setFullMode(!fullMode);
                            if (!fullMode) setIframeLoading(true);
                        }}
                    >
                        {fullMode ? '↙ ' + T._('Quick mode', 'Admin') : '↗ ' + T._('Full mode', 'Admin')}
                    </FullModeLink>
                    {!fullMode && (
                        <StackedFooterButtons>
                            <FooterButton onClick={onBack}>{T._('Cancel', 'Admin')}</FooterButton>
                            <FooterButtonTooltip>
                                <ZTooltip title={!isPlanSupported ? T._('Upgrade your plan to enable this feature', 'Admin') : undefined} addContainer>
                                    <FooterButton $primary disabled={!dirty || saving || !isPlanSupported} onClick={handleSave}>
                                        {saving ? T._('Saving...', 'Admin') : T._('Save Changes', 'Admin')}
                                    </FooterButton>
                                </ZTooltip>
                            </FooterButtonTooltip>
                        </StackedFooterButtons>
                    )}
                </>
            }
        >
            {fullMode ? (
                <FullModeIframeContainer>
                    {iframeLoading && (
                        <FullModeSkeleton>
                            <FullModeEditorSkeleton />
                        </FullModeSkeleton>
                    )}
                    <IframeEmbed
                        src={backofficeUrl('/pricing-rule-embedded/?pricing_id=' + toLocalID(pricingId)) + '&embedded=true'}
                        $loading={iframeLoading}
                        title={pricingName}
                        onLoad={() => setIframeLoading(false)}
                    />
                </FullModeIframeContainer>
            ) : (
                <>
                    <StackedBreadcrumb>
                        <span onClick={onBack}>{productName}</span> / {T._('Pricing rules', 'Admin')} / {pricingName}
                    </StackedBreadcrumb>

                    <StackedBody>
                        {!isPlanSupported && (
                            <PlanUpgradeBanner
                                message={T._(
                                    'Your current plan does not include Advanced Pricing. This rule cannot be edited until you upgrade your plan.',
                                    'Admin'
                                )}
                            />
                        )}
                        {assignedProductCount != null && assignedProductCount > 1 && (
                            <SharedWarningCallout>
                                {T._('This pricing rule is assigned to', 'Admin')} <strong>{assignedProductCount}</strong>{' '}
                                {T._('products. Any changes will apply to all of them.', 'Admin')}
                            </SharedWarningCallout>
                        )}
                        {!pricingGroup ? (
                            <EmptyRulesCenter>{T._('No rules configured', 'Admin')}</EmptyRulesCenter>
                        ) : (
                            <>
                                {/* ---- RULES ---- */}
                                <SectionCard>
                                    <SectionCardTitle>{T._('Rules', 'Admin')}</SectionCardTitle>

                                    {pricingGroup.rules.length === 0 ? (
                                        <EmptyRulesSmall>{T._('No rules configured', 'Admin')}</EmptyRulesSmall>
                                    ) : (
                                        pricingGroup.rules.map((rule) => {
                                            const isExpanded = expandedRules.has(rule.tempID);
                                            return (
                                                <PricingRuleCard key={rule.tempID}>
                                                    <PricingRuleCardHeader onClick={() => toggleRule(rule.tempID)}>
                                                        <div>
                                                            <RulePropertyName>{rule.propertyName}</RulePropertyName>
                                                            {rule.elementTitle && <RuleElementTitle>{rule.elementTitle}</RuleElementTitle>}
                                                            <RuleMetaLine>
                                                                {rule.conditions.length}{' '}
                                                                {rule.conditions.length === 1 ? T._('condition', 'Admin') : T._('conditions', 'Admin')}
                                                                {' · '}
                                                                {rule.isSetupPrice ? T._('Setup', 'Admin') : T._('Design', 'Admin')}
                                                                {' · '}
                                                                {rule.priceType === 1 ? '%' : T._('Fixed', 'Admin')}
                                                                {' · '}
                                                                {rule.allMatchType ? T._('All match', 'Admin') : T._('First match', 'Admin')}
                                                            </RuleMetaLine>
                                                        </div>
                                                        <RuleHeaderActions>
                                                            <DeleteRuleButton
                                                                onClick={(element) => {
                                                                    element.stopPropagation();
                                                                    deleteRule(rule.tempID);
                                                                }}
                                                                title={T._('Delete rule', 'Admin')}
                                                            >
                                                                &#10005;
                                                            </DeleteRuleButton>
                                                            <ExpandArrow>{isExpanded ? '▴' : '▾'}</ExpandArrow>
                                                        </RuleHeaderActions>
                                                    </PricingRuleCardHeader>
                                                    {isExpanded && (
                                                        <PricingRuleCardBody>
                                                            {/* Rule settings */}
                                                            <FormGridRow $columns={2}>
                                                                <div>
                                                                    <FormLabel>{T._('Property', 'Admin')}</FormLabel>
                                                                    <FormSelectCompact
                                                                        value={rule.propertyID}
                                                                        onChange={(element) => {
                                                                            const newPropId = Number(element.target.value);
                                                                            const newPropName =
                                                                                properties.find((pricing) => pricing.propertyID === newPropId)?.propertyName ||
                                                                                `Property #${newPropId}`;
                                                                            setPricingGroup((prev) => {
                                                                                if (!prev) return prev;
                                                                                return {
                                                                                    ...prev,
                                                                                    rules: prev.rules.map((r) =>
                                                                                        r.tempID === rule.tempID
                                                                                            ? {
                                                                                                  ...r,
                                                                                                  propertyID: newPropId,
                                                                                                  propertyName: newPropName,
                                                                                                  elementID: 1,
                                                                                                  elementTitle: null
                                                                                              }
                                                                                            : r
                                                                                    )
                                                                                };
                                                                            });
                                                                            setDirty(true);
                                                                        }}
                                                                    >
                                                                        {properties.map((pricing) => (
                                                                            <option key={pricing.propertyID} value={pricing.propertyID}>
                                                                                {pricing.propertyName}
                                                                            </option>
                                                                        ))}
                                                                    </FormSelectCompact>
                                                                </div>
                                                                <div>
                                                                    <FormLabel>{T._('Element', 'Admin')}</FormLabel>
                                                                    {(() => {
                                                                        const prop = properties.find((pricing) => pricing.propertyID === rule.propertyID);
                                                                        const elements = prop?.elements || [];
                                                                        return (
                                                                            <FormSelectCompact
                                                                                value={rule.elementID || 1}
                                                                                onChange={(element) => {
                                                                                    const elId = Number(element.target.value);
                                                                                    const elName =
                                                                                        elements.find((el) => el.elementID === elId)?.elementName || null;
                                                                                    setPricingGroup((prev) => {
                                                                                        if (!prev) return prev;
                                                                                        return {
                                                                                            ...prev,
                                                                                            rules: prev.rules.map((r) =>
                                                                                                r.tempID === rule.tempID
                                                                                                    ? { ...r, elementID: elId, elementTitle: elName }
                                                                                                    : r
                                                                                            )
                                                                                        };
                                                                                    });
                                                                                    setDirty(true);
                                                                                }}
                                                                            >
                                                                                {elements.map((el) => (
                                                                                    <option key={el.elementID} value={el.elementID}>
                                                                                        {el.elementName}
                                                                                    </option>
                                                                                ))}
                                                                            </FormSelectCompact>
                                                                        );
                                                                    })()}
                                                                </div>
                                                            </FormGridRow>

                                                            <FormGridRow $columns={3} style={{ marginTop: 18 }}>
                                                                <div>
                                                                    <FormLabel>{T._('Type', 'Admin')}</FormLabel>
                                                                    <FormSelectCompact
                                                                        value={rule.isSetupPrice ? 'setup' : 'design'}
                                                                        onChange={(element) =>
                                                                            updateRuleField(rule.tempID, 'isSetupPrice', element.target.value === 'setup')
                                                                        }
                                                                    >
                                                                        <option value='setup'>{T._('Setup', 'Admin')}</option>
                                                                        <option value='design'>{T._('Design', 'Admin')}</option>
                                                                    </FormSelectCompact>
                                                                </div>
                                                                <div>
                                                                    <FormLabel>{T._('Match', 'Admin')}</FormLabel>
                                                                    <FormSelectCompact
                                                                        value={rule.allMatchType ? 'all' : 'first'}
                                                                        onChange={(element) =>
                                                                            updateRuleField(rule.tempID, 'allMatchType', element.target.value === 'all')
                                                                        }
                                                                    >
                                                                        <option value='all'>{T._('All', 'Admin')}</option>
                                                                        <option value='first'>{T._('First match', 'Admin')}</option>
                                                                    </FormSelectCompact>
                                                                </div>
                                                                <div>
                                                                    <FormLabel>{T._('Price type', 'Admin')}</FormLabel>
                                                                    <FormSelectCompact
                                                                        value={rule.priceType}
                                                                        onChange={(element) =>
                                                                            updateRuleField(rule.tempID, 'priceType', Number(element.target.value))
                                                                        }
                                                                    >
                                                                        <option value={0}>{T._('Fixed', 'Admin')}</option>
                                                                        <option value={1}>{T._('Percentage', 'Admin')}</option>
                                                                    </FormSelectCompact>
                                                                </div>
                                                            </FormGridRow>

                                                            {/* Conditions */}
                                                            <FormLabelCompact style={{ marginTop: 18 }}>{T._('Conditions', 'Admin')}</FormLabelCompact>
                                                            {rule.conditions.length === 0 && <EmptyConditions>{T._('No conditions', 'Admin')}</EmptyConditions>}
                                                            {(() => {
                                                                // Group conditions by formula for quantity tiers
                                                                const groups: { key: string; formula: string; conditions: PricingConditionData[] }[] = [];
                                                                rule.conditions.forEach((cond) => {
                                                                    const hasQty = Number(cond.quantity) > 0 || cond.quantity === '';
                                                                    const key = hasQty ? `f:${cond.formula}` : `c:${cond.tempID}`;
                                                                    const existing = groups.find((g) => g.key === key);
                                                                    if (existing) {
                                                                        existing.conditions.push(cond);
                                                                    } else {
                                                                        groups.push({
                                                                            key,
                                                                            formula: cond.formula,
                                                                            conditions: [cond]
                                                                        });
                                                                    }
                                                                });

                                                                return groups.map((group, groupIndex) => {
                                                                    const parsed = parseFormula(group.formula);
                                                                    const hasQuantityTiers = !(
                                                                        group.conditions.length === 1 && Number(group.conditions[0].quantity) === 0
                                                                    );
                                                                    const connector =
                                                                        CONDITION_OPERATORS.find((op) => op.value === parsed.operator)?.connector || 'to';
                                                                    return (
                                                                        <div key={groupIndex} style={{ marginBottom: 14 }}>
                                                                            {/* If (element) is [condition] [connector] [value] Add [price] */}
                                                                            <ConditionFormRow>
                                                                                <ConditionLabel>
                                                                                    {T._('If', 'Admin')}{' '}
                                                                                    <em>
                                                                                        <strong>{getPropertyKeyword(rule.propertyID)}</strong>
                                                                                    </em>{' '}
                                                                                    {T._('is', 'Admin')}
                                                                                </ConditionLabel>
                                                                                <div style={{ flex: 2, minWidth: 0 }}>
                                                                                    <ConditionFieldTitle>{T._('Condition', 'Admin')}</ConditionFieldTitle>
                                                                                    <FormSelect
                                                                                        value={parsed.operator}
                                                                                        onChange={(element) => {
                                                                                            const newFormula = buildFormula(
                                                                                                element.target.value,
                                                                                                parsed.values
                                                                                            );
                                                                                            const updatedConditions = rule.conditions.map((condition) =>
                                                                                                condition.formula === group.formula
                                                                                                    ? { ...condition, formula: newFormula }
                                                                                                    : condition
                                                                                            );
                                                                                            updateRuleField(rule.tempID, 'conditions', updatedConditions);
                                                                                        }}
                                                                                        style={{
                                                                                            fontSize: 11,
                                                                                            padding: '3px 4px',
                                                                                            width: '100%'
                                                                                        }}
                                                                                    >
                                                                                        {CONDITION_OPERATORS.map((op) => (
                                                                                            <option key={op.value} value={op.value}>
                                                                                                {op.label}
                                                                                            </option>
                                                                                        ))}
                                                                                    </FormSelect>
                                                                                </div>
                                                                                <ConditionLabel $minWidth={20}>{connector}</ConditionLabel>
                                                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                                                    <ConditionFieldTitle>{T._('Value', 'Admin')}</ConditionFieldTitle>
                                                                                    <PriceInput
                                                                                        type='number'
                                                                                        placeholder={T._('Value', 'Admin')}
                                                                                        value={parsed.values[0] || ''}
                                                                                        onChange={(element) => {
                                                                                            const newFormula = buildFormula(parsed.operator, [
                                                                                                element.target.value
                                                                                            ]);
                                                                                            const updatedConditions = rule.conditions.map((condition) =>
                                                                                                condition.formula === group.formula
                                                                                                    ? { ...condition, formula: newFormula }
                                                                                                    : condition
                                                                                            );
                                                                                            updateRuleField(rule.tempID, 'conditions', updatedConditions);
                                                                                        }}
                                                                                        style={{ width: '100%' }}
                                                                                    />
                                                                                </div>
                                                                                <ConditionLabel
                                                                                    $minWidth={20}
                                                                                    style={hasQuantityTiers ? { visibility: 'hidden' } : undefined}
                                                                                >
                                                                                    {T._('add', 'Admin')}
                                                                                </ConditionLabel>
                                                                                <div
                                                                                    style={
                                                                                        hasQuantityTiers
                                                                                            ? { flex: 1, minWidth: 0, visibility: 'hidden' }
                                                                                            : { flex: 1, minWidth: 0 }
                                                                                    }
                                                                                >
                                                                                    <ConditionFieldTitle>
                                                                                        {rule.priceType === 1 ? '%' : T._('Price', 'Admin')}
                                                                                    </ConditionFieldTitle>
                                                                                    <PriceInput
                                                                                        type='number'
                                                                                        step='0.01'
                                                                                        value={group.conditions[0]?.priceToAdd ?? ''}
                                                                                        onChange={(element) =>
                                                                                            updateConditionField(
                                                                                                rule.tempID,
                                                                                                group.conditions[0].tempID,
                                                                                                'priceToAdd',
                                                                                                element.target.value
                                                                                            )
                                                                                        }
                                                                                        style={{ width: '100%' }}
                                                                                        placeholder={T._('Price', 'Admin')}
                                                                                        tabIndex={hasQuantityTiers ? -1 : undefined}
                                                                                    />
                                                                                </div>
                                                                                <AddQuantityButton
                                                                                    type='button'
                                                                                    style={hasQuantityTiers ? { visibility: 'hidden' } : undefined}
                                                                                    onClick={() => {
                                                                                        const updated = rule.conditions.map((condition) =>
                                                                                            condition.tempID === group.conditions[0].tempID
                                                                                                ? { ...condition, quantity: '1' }
                                                                                                : condition
                                                                                        );
                                                                                        updateRuleField(rule.tempID, 'conditions', updated);
                                                                                    }}
                                                                                    title={T._('Add quantity tiers', 'Admin')}
                                                                                    tabIndex={hasQuantityTiers ? -1 : undefined}
                                                                                >
                                                                                    +qty
                                                                                </AddQuantityButton>
                                                                                <DeleteRuleButton
                                                                                    onClick={() => {
                                                                                        updateRuleField(
                                                                                            rule.tempID,
                                                                                            'conditions',
                                                                                            rule.conditions.filter(
                                                                                                (condition) => condition.formula !== group.formula
                                                                                            )
                                                                                        );
                                                                                    }}
                                                                                    title={T._('Delete condition', 'Admin')}
                                                                                >
                                                                                    &#10005;
                                                                                </DeleteRuleButton>
                                                                            </ConditionFormRow>

                                                                            {/* Quantity tiers */}
                                                                            {hasQuantityTiers && (
                                                                                <QuantityTierContainer>
                                                                                    <a
                                                                                        href='https://zakeke.zendesk.com/hc/en-us/articles/360015984779-How-to-create-pricing-rules#h_01G7RQN5VMC8ABXJMHP4RG7MYB'
                                                                                        target='_blank'
                                                                                        rel='noreferrer'
                                                                                        style={{
                                                                                            fontSize: 11,
                                                                                            color: '#E8650A',
                                                                                            display: 'block',
                                                                                            marginBottom: 6
                                                                                        }}
                                                                                    >
                                                                                        {T._('How product quantity works', 'Admin')} &#8599;
                                                                                    </a>
                                                                                    {[...group.conditions]
                                                                                        .sort((a, b) => {
                                                                                            const qa = a.quantity === '' ? Infinity : Number(a.quantity);
                                                                                            const qb = b.quantity === '' ? Infinity : Number(b.quantity);
                                                                                            return qa - qb;
                                                                                        })
                                                                                        .map((tier) => (
                                                                                            <QuantityTierRow key={tier.tempID}>
                                                                                                <ConditionLabel $minWidth={26}>
                                                                                                    {T._('From', 'Admin')}
                                                                                                </ConditionLabel>
                                                                                                <div>
                                                                                                    <ConditionFieldTitle>
                                                                                                        {T._('Quantity', 'Admin')}
                                                                                                    </ConditionFieldTitle>
                                                                                                    <PriceInput
                                                                                                        type='number'
                                                                                                        value={tier.quantity}
                                                                                                        onChange={(element) =>
                                                                                                            updateConditionField(
                                                                                                                rule.tempID,
                                                                                                                tier.tempID,
                                                                                                                'quantity',
                                                                                                                element.target.value
                                                                                                            )
                                                                                                        }
                                                                                                        style={{ width: '100%' }}
                                                                                                        placeholder='0'
                                                                                                    />
                                                                                                </div>
                                                                                                <ConditionLabel $minWidth={20}>
                                                                                                    {T._('add', 'Admin')}
                                                                                                </ConditionLabel>
                                                                                                <div>
                                                                                                    <ConditionFieldTitle>
                                                                                                        {rule.priceType === 1 ? '%' : T._('Price', 'Admin')}
                                                                                                    </ConditionFieldTitle>
                                                                                                    <PriceInput
                                                                                                        type='number'
                                                                                                        step='0.01'
                                                                                                        value={tier.priceToAdd}
                                                                                                        onChange={(element) =>
                                                                                                            updateConditionField(
                                                                                                                rule.tempID,
                                                                                                                tier.tempID,
                                                                                                                'priceToAdd',
                                                                                                                element.target.value
                                                                                                            )
                                                                                                        }
                                                                                                        style={{ width: '100%' }}
                                                                                                    />
                                                                                                </div>
                                                                                                <DeleteRuleButton
                                                                                                    onClick={() => {
                                                                                                        const remaining = group.conditions.filter(
                                                                                                            (condition) => condition.tempID !== tier.tempID
                                                                                                        );
                                                                                                        if (remaining.length <= 1) {
                                                                                                            // Last or second-to-last tier: keep the remaining condition, reset quantity to 0
                                                                                                            const survivorId =
                                                                                                                remaining.length === 1
                                                                                                                    ? remaining[0].tempID
                                                                                                                    : tier.tempID;
                                                                                                            const updated = rule.conditions
                                                                                                                .filter(
                                                                                                                    (condition) =>
                                                                                                                        condition.formula !== group.formula ||
                                                                                                                        condition.tempID === survivorId
                                                                                                                )
                                                                                                                .map((condition) =>
                                                                                                                    condition.tempID === survivorId
                                                                                                                        ? { ...condition, quantity: '0' }
                                                                                                                        : condition
                                                                                                                );
                                                                                                            updateRuleField(rule.tempID, 'conditions', updated);
                                                                                                        } else {
                                                                                                            updateRuleField(
                                                                                                                rule.tempID,
                                                                                                                'conditions',
                                                                                                                rule.conditions.filter(
                                                                                                                    (condition) =>
                                                                                                                        condition.tempID !== tier.tempID
                                                                                                                )
                                                                                                            );
                                                                                                        }
                                                                                                    }}
                                                                                                >
                                                                                                    &#10005;
                                                                                                </DeleteRuleButton>
                                                                                            </QuantityTierRow>
                                                                                        ))}
                                                                                    <AddTextButton
                                                                                        type='button'
                                                                                        onClick={() => {
                                                                                            const newTier: PricingConditionData = {
                                                                                                conditionID: null,
                                                                                                tempID: crypto.randomUUID(),
                                                                                                formula: group.formula,
                                                                                                position: group.conditions[0].position,
                                                                                                priceToAdd: '',
                                                                                                quantity: ''
                                                                                            };
                                                                                            updateRuleField(rule.tempID, 'conditions', [
                                                                                                ...rule.conditions,
                                                                                                newTier
                                                                                            ]);
                                                                                        }}
                                                                                    >
                                                                                        <Icon>
                                                                                            <PlusIcon />
                                                                                        </Icon>
                                                                                        {T._('Add tier', 'Admin')}
                                                                                    </AddTextButton>
                                                                                </QuantityTierContainer>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                });
                                                            })()}

                                                            <AddTextButton
                                                                type='button'
                                                                onClick={() => {
                                                                    const newCond: PricingConditionData = {
                                                                        conditionID: null,
                                                                        tempID: crypto.randomUUID(),
                                                                        formula: buildFormula('=', ['']),
                                                                        position: rule.conditions.length,
                                                                        priceToAdd: '0',
                                                                        quantity: '0'
                                                                    };
                                                                    updateRuleField(rule.tempID, 'conditions', [...rule.conditions, newCond]);
                                                                }}
                                                            >
                                                                <Icon>
                                                                    <PlusIcon />
                                                                </Icon>
                                                                {T._('Add condition', 'Admin')}
                                                            </AddTextButton>

                                                            {/* Targets summary */}
                                                            {rule.targets.length > 0 && (
                                                                <TargetsSection>
                                                                    <FormLabel>{T._('Targets', 'Admin')}</FormLabel>
                                                                    <TargetsInfo>
                                                                        {rule.targets.map((target, targetIndex) => (
                                                                            <span key={target.targetID}>
                                                                                {[target.area, target.color, target.side].filter(Boolean).join(' / ') ||
                                                                                    T._('All', 'Admin')}
                                                                                {targetIndex < rule.targets.length - 1 && ', '}
                                                                            </span>
                                                                        ))}
                                                                        <SettingHint>{T._('Target editing in full mode', 'Admin')}</SettingHint>
                                                                    </TargetsInfo>
                                                                </TargetsSection>
                                                            )}
                                                        </PricingRuleCardBody>
                                                    )}
                                                </PricingRuleCard>
                                            );
                                        })
                                    )}

                                    {/* Add rule button */}
                                    <AddRuleButton
                                        type='button'
                                        onClick={() => {
                                            if (!pricingGroup || properties.length === 0) return;
                                            const firstProp = properties[0];
                                            const newRule: PricingRuleData = {
                                                ruleID: null,
                                                tempID: crypto.randomUUID(),
                                                propertyID: firstProp.propertyID,
                                                propertyName: firstProp.propertyName,
                                                elementID: 1,
                                                elementTitle: null,
                                                isSetupPrice: false,
                                                allMatchType: true,
                                                priceType: 0,
                                                simplified: false,
                                                conditions: [
                                                    {
                                                        conditionID: null,
                                                        tempID: crypto.randomUUID(),
                                                        formula: buildFormula('=', ['']),
                                                        position: 0,
                                                        priceToAdd: '0',
                                                        quantity: '0'
                                                    }
                                                ],
                                                targets: []
                                            };
                                            setPricingGroup((prev) => {
                                                if (!prev) return prev;
                                                return { ...prev, rules: [...prev.rules, newRule] };
                                            });
                                            setExpandedRules((prev) => new Set([...prev, newRule.tempID]));
                                            setDirty(true);
                                        }}
                                    >
                                        <Icon>
                                            <PlusIcon />
                                        </Icon>
                                        {T._('Add rule', 'Admin')}
                                    </AddRuleButton>
                                </SectionCard>
                            </>
                        )}
                    </StackedBody>
                </>
            )}
        </Drawer>
    );
};

export const PricingRuleEditor: FC<PricingRuleEditorProps> = (props) => {
    return (
        <Suspense
            fallback={
                <Drawer isOpen={true} isStacked onClose={props.onBack} header={{ title: '', subtitle: T._('Pricing rules', 'Admin') }} width={620}>
                    <StackedBreadcrumb>
                        <span onClick={props.onBack}>&nbsp;</span> / {T._('Pricing rules', 'Admin')}
                    </StackedBreadcrumb>
                    <StackedBody>
                        <PanelFormSkeleton />
                    </StackedBody>
                </Drawer>
            }
        >
            <PricingRuleEditorContent {...props} />
        </Suspense>
    );
};
